import { morph } from '@theme/morph'
import { Component } from '@theme/component'
import { CartUpdateEvent, ThemeEvents } from '@theme/events'
import { DialogComponent, DialogCloseEvent } from '@theme/dialog'
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities'

export class QuickShopComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null
  /** @type {Document | null} */
  #cachedProductHtml = null

  #productPageUrl = ''
  
  /** @type {HTMLElement | null} */
  #quickShopButton = null

  get cachedProductHtml() {
    return this.#cachedProductHtml
  }

  connectedCallback() {
    super.connectedCallback()

    this.#productPageUrl = /** @type {HTMLAnchorElement} */ (
      this.closest('product-card')?.querySelector('a[ref="productCardLink"]')
    )?.href
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal)
    
    // Mobile fix: Add direct touch handler to prevent double-tap issue
    if ('ontouchstart' in window) {
      this.#quickShopButton = this.querySelector('button.quick-shop__button')
      if (this.#quickShopButton) {
        this.#quickShopButton.addEventListener('touchend', this.#handleTouchEnd, { passive: false })
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal)
    
    // Clean up touch handler
    if (this.#quickShopButton) {
      this.#quickShopButton.removeEventListener('touchend', this.#handleTouchEnd)
    }
  }

  /**
   * Handles touch end event on mobile to prevent double-tap issue
   * @param {TouchEvent} event - The touch event
   */
  #handleTouchEnd = (event) => {
    event.preventDefault()
    event.stopPropagation()
    this.handleClick(event)
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault()

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage()
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      )

      await this.updateQuickAddModal(freshHtmlCopy)
    }

    this.#openQuickAddModal()
  }

  /** @param {Element} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true)

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      {
        once: true,
      }
    )
  }

  #openQuickAddModal = () => {
    const cartDrawer = /** @type {QuickShopDialog | null} */ (
      document.getElementById('cart-drawer')
    )
    if (!cartDrawer) {
      return
    }

    this.#stayVisibleUntilDialogCloses(cartDrawer)

    // Use the cart drawer component's showDialog method
    if (typeof cartDrawer.showDialog === 'function') {
      cartDrawer.showDialog()
      document.documentElement.setAttribute('scroll-lock', '')
    }
  }

  #closeQuickAddModal = () => {
    const cartDrawer = /** @type {QuickShopDialog | null} */ (
      document.getElementById('cart-drawer')
    )
    if (!cartDrawer) return

    if (typeof cartDrawer.closeDialog === 'function') {
      cartDrawer.closeDialog()
    }
  }

  /**
   * Fetches the product page content
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage() {
    if (!this.#productPageUrl) return

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort()
    this.#abortController = new AbortController()

    try {
      const response = await fetch(this.#productPageUrl, {
        signal: this.#abortController.signal,
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        )
      }

      const responseText = await response.text()
      const html = new DOMParser().parseFromString(responseText, 'text/html')

      // Store the HTML for later use
      this.#cachedProductHtml = html
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      } else {
        throw error
      }
    } finally {
      this.#abortController = null
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]')
    const productColors = newHtml.querySelector('.product-siblings')
    const modalContent = document.getElementById(
      'quick_add_modal_content_product'
    )
    if (!productGrid || !modalContent) return

    const variantPicker = productGrid.querySelector('variant-picker')
    if (variantPicker) {
      variantPicker.setAttribute(
        'data-product-form-selector',
        '#cart-drawer product-form-component'
      )
      variantPicker.setAttribute(
        'data-bis-button-wrapper-selector',
        '#cart-drawer .variant-picker .product-notification,#cart-drawer .product-inventory .product-notification'
      )
    }
    if (productColors) {
      productColors.classList.add('product-custom-colors')
      productColors.setAttribute(
        'data-product-color-picker-selector',
        '#cart-drawer color-picker'
      )
    }

    const productFormComponent = productGrid.querySelector(
      'product-form-component'
    )

    const viewDetailsLink = document.createElement('a')
    viewDetailsLink.className = 'notify-link view-full-details'
    viewDetailsLink.textContent = 'View Full Details'
    viewDetailsLink.href = this.#productPageUrl

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details')
      if (!productDetails) return

      const productPrice = productGrid.querySelector('product-price')
      const productTitle = document.createElement('a')
      productTitle.textContent = this.dataset.productTitle || ''
      productTitle.href = this.#productPageUrl

      if (
        !productFormComponent ||
        !variantPicker ||
        !productPrice ||
        !productTitle
      )
        return

      const productHeader = document.createElement('div')
      productHeader.classList.add('product-header')
      productHeader.appendChild(productTitle)
      productHeader.appendChild(productPrice)

      productGrid.appendChild(productHeader)
      if (productColors) {
        const productColorsClone = productColors.cloneNode(true)
        productGrid.appendChild(productColorsClone)
      }
      productGrid.appendChild(variantPicker)
      productGrid.appendChild(productFormComponent)

      productFormComponent.appendChild(viewDetailsLink)

      productDetails.remove()
    } else {
      if (productFormComponent) {
        productFormComponent.appendChild(viewDetailsLink)
      }
    }

    const kiwiSizing = productGrid.querySelector('kiwi-sizing-chart')
    if (kiwiSizing) kiwiSizing.remove()

    // 2) Re-init media-gallery & its inner slideshow by recreating it
    const galleries = modalContent.querySelectorAll('media-gallery')
    galleries.forEach((gallery) => {
      const cloned = gallery.cloneNode(true)
      gallery.replaceWith(cloned)
    })

    morph(modalContent, productGrid)
  }
}

if (!customElements.get('quick-shop-component')) {
  customElements.define('quick-shop-component', QuickShopComponent)
}

class QuickShopDialog extends DialogComponent {
  #abortController = new AbortController()

  connectedCallback() {
    super.connectedCallback()

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    })
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink,
      {
        signal: this.#abortController.signal,
      }
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.#abortController.abort()
  }
  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return
    this.closeDialog()
    document.documentElement.removeAttribute('scroll-lock')
  }

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    )
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.view-product-title a')
    )
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.product-header a')
    )

    if (!anchorElement) return

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href
  }
}

// TODO: BIS (Back In Stock) integration for quick-add modal
// Currently disabled due to app limitation - BIS.popup.show() only works on PDPs, not collection pages
// Future enhancement: Create custom modal and use BIS.create() API directly
// For now, "Notify me" buttons are hidden in quick-add modals via CSS

document.addEventListener('DOMContentLoaded', function () {
  const quickAddModal = document.querySelector('.quick-add-modal')
  if (quickAddModal) {
    // Hide "Notify me" buttons in quick-add modals since BIS popup doesn't work on collection pages
    const bisButtons = quickAddModal.querySelectorAll(
      '.product-notification .bis'
    )
    bisButtons.forEach((button) => {
      if (button instanceof HTMLElement) {
        button.style.display = 'none'
      }
    })
  }
})

if (!customElements.get('quick-shop-dialog')) {
  customElements.define('quick-shop-dialog', QuickShopDialog)
}
